import { useState, useCallback } from "react";
import CustomCollectionsService from "@/services/CustomCollectionsServiceNew";

/**
 * useCustomCollections Hook - Comprehensive hook for managing custom collections data
 * Handles filtering, sorting, and pagination for custom collections
 */
export default function useCustomCollections() {
  // Data State
  const [customCollectionsData, setCustomCollectionsData] = useState([]);
  
  // UI State
  const [loadingCustomCollections, setLoadingCustomCollections] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Error/Message State
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  
  // Pagination State
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  
  // Filter/Sort State
  const [filters, setFilters] = useState({});
  const [sortColumn, setSortColumn] = useState('created');
  const [sortDirection, setSortDirection] = useState('desc');
  const [limit, setLimit] = useState(5);

  /**
   * Load custom collections with current filters and pagination
   */
  const loadCustomCollectionsData = useCallback(async () => {
    // console.log(`Loading custom collections data, limit: ${limit} filters: `, filters);
    setLoadingMore(prevLoadingMore => {
      if (!prevLoadingMore) setLoadingCustomCollections(true);
      return prevLoadingMore;
    });
    setError(null);

    const result = await CustomCollectionsService.getCustomCollectionsWithFilters({
      limit,
      sortColumn,
      sortDirection,
      filters
    });

    // console.log('Custom collections data result:', result);
    
    if (result.error) {
      setError(result.error);
      setCustomCollectionsData([]);
      setTotalCount(0);
      setHasMore(false);
    } else {
      setCustomCollectionsData(result.data);
      setTotalCount(result.totalCount);
      setHasMore(result.hasMore);
    }
    
    setLoadingCustomCollections(false);
    setLoadingMore(false);
  }, [limit, sortColumn, sortDirection, filters]);

  /**
   * Load more custom collections (pagination)
   */
  const handleLoadMore = useCallback(() => {
    if (hasMore && !loadingMore) {
      setLimit(prevLimit => prevLimit + 50);
      setLoadingMore(true);
    }
  }, [hasMore, loadingMore]);

  /**
   * Update sorting
   */
  const setSorting = useCallback((column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
  }, []);

  /**
   * Update filters
   */
  const updateFilters = useCallback((newFilters) => {
    setFilters(prevFilters => ({ ...prevFilters, ...newFilters }));
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters({});
    // setLimit(50);
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clear message state
   */
  const clearMessage = useCallback(() => {
    setMessage(null);
  }, []);

  return {
    // Data
    customCollectionsData,
    totalCount,
    hasMore,
    
    // UI States
    loadingCustomCollections,
    loadingMore,
    error,
    message,
    
    // Filter/Sort State
    filters,
    sortColumn,
    sortDirection,
    limit,
    
    // Actions
    loadCustomCollectionsData,
    handleLoadMore,
    setCustomCollectionsSorting: setSorting,
    updateFilters,
    clearFilters,
    
    // State management
    setFilters,
    setError: clearError,
    setMessage: clearMessage
  };
}